import * as React from 'react';
import { Box, Popover } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { HXCIcon, Icons } from '../HXCIcon';

export type MenuFloatingTopperAlign = 'anchor' | 'panelCenter';

/**
 * `adaptive`: use `scrollbar-gutter: stable` only when the list overflows (full-width row highlights when short; no layout jump when a scrollbar appears). `auto` / `stable`: force that
 * mode for all states.
 */
export type MenuScrollbarGutter = 'adaptive' | 'auto' | 'stable';

export interface MenuProps {
  anchorEl: HTMLElement | null;
  anchorElId: string;
  handleClose: () => void;
  menuList: React.ReactNode;
  /**
   * `anchor`: point the triangle at the trigger centre (e.g. header “My Account”).
   * `panelCenter`: centre the triangle on the menu panel (e.g. order builder overflow menu).
   */
  floatingTopperAlign?: MenuFloatingTopperAlign;
  /** @default 'adaptive' */
  scrollbarGutter?: MenuScrollbarGutter;
}

/** Matches `floating_menu_topper.svg` width / height. */
const FLOATING_MENU_TOPPER_WIDTH_PX = 32;
const FLOATING_MENU_TOPPER_HEIGHT_PX = 15;
const MENU_PANEL_CORNER_RADIUS = '4px';
/** Scrollport radius inset by the border width so the gutter follows the inner curve. */
const MENU_PANEL_SCROLL_INNER_CORNER_RADIUS = '3px';

const SCROLL_OVERFLOW_EPSILON_PX = 1;

const menuScrollPortOverflows = (scrollPortElement: HTMLElement) =>
  scrollPortElement.scrollHeight > scrollPortElement.clientHeight + SCROLL_OVERFLOW_EPSILON_PX;

const alignFloatingTopperToAnchorCentre = (params: {
  anchorElement: HTMLElement;
  chromeElement: HTMLElement;
  notchElement: HTMLElement;
}) => {
  const { anchorElement, chromeElement, notchElement } = params;
  const anchorRect = anchorElement.getBoundingClientRect();
  const chromeRect = chromeElement.getBoundingClientRect();
  const anchorCentrePx = anchorRect.left + anchorRect.width / 2;
  const leftPx = anchorCentrePx - chromeRect.left;
  notchElement.style.left = `${leftPx}px`;
  notchElement.style.right = 'auto';
  notchElement.style.transform = 'translateX(-50%)';
};

export const Menu: React.FC<MenuProps> = ({
  anchorEl,
  anchorElId,
  handleClose,
  menuList,
  floatingTopperAlign = 'anchor',
  scrollbarGutter = 'adaptive',
}) => {
  const theme = useTheme();
  const chromeRef = React.useRef<HTMLDivElement | null>(null);
  const notchRef = React.useRef<HTMLDivElement | null>(null);
  const scrollPortRef = React.useRef<HTMLDivElement | null>(null);
  const [adaptiveScrollbarGutter, setAdaptiveScrollbarGutter] = React.useState<'auto' | 'stable'>(
    'auto',
  );
  const isOpen = Boolean(anchorEl);

  const viewportGutter = theme.spacing(3);
  const scrollableMaxHeight = `calc(100dvh - ${viewportGutter} - ${FLOATING_MENU_TOPPER_HEIGHT_PX}px)`;

  const runAnchorTopperAlignment = React.useCallback(() => {
    if (floatingTopperAlign !== 'anchor' || !anchorEl || !chromeRef.current || !notchRef.current) {
      return;
    }
    alignFloatingTopperToAnchorCentre({
      anchorElement: anchorEl,
      chromeElement: chromeRef.current,
      notchElement: notchRef.current,
    });
  }, [anchorEl, floatingTopperAlign]);

  const recalcAdaptiveScrollbarGutter = React.useCallback(() => {
    if (scrollbarGutter !== 'adaptive') {
      return;
    }
    const scrollPort = scrollPortRef.current;
    if (!scrollPort) {
      return;
    }
    setAdaptiveScrollbarGutter(menuScrollPortOverflows(scrollPort) ? 'stable' : 'auto');
  }, [scrollbarGutter]);

  React.useLayoutEffect(() => {
    if (!isOpen) {
      if (scrollbarGutter === 'adaptive') {
        setAdaptiveScrollbarGutter('auto');
      }
      return;
    }
    if (scrollbarGutter !== 'adaptive') {
      return;
    }

    recalcAdaptiveScrollbarGutter();

    const scrollPort = scrollPortRef.current;
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' && scrollPort
        ? new ResizeObserver(() => {
            recalcAdaptiveScrollbarGutter();
          })
        : null;
    if (resizeObserver && scrollPort) {
      resizeObserver.observe(scrollPort);
    }

    return () => {
      resizeObserver?.disconnect();
    };
  }, [isOpen, recalcAdaptiveScrollbarGutter, scrollbarGutter]);

  const resolvedScrollbarGutter =
    scrollbarGutter === 'adaptive' ? adaptiveScrollbarGutter : scrollbarGutter;

  React.useEffect(() => {
    // Popover applies `left` / `top` in a passive effect after paint; useLayoutEffect
    // would measure the panel before it is positioned (notch stuck in the wrong place).
    runAnchorTopperAlignment();

    if (!isOpen || floatingTopperAlign !== 'anchor' || !chromeRef.current) {
      return;
    }

    const chromeNode = chromeRef.current;
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(runAnchorTopperAlignment) : null;
    resizeObserver?.observe(chromeNode);

    window.addEventListener('resize', runAnchorTopperAlignment);
    window.addEventListener('scroll', runAnchorTopperAlignment, true);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', runAnchorTopperAlignment);
      window.removeEventListener('scroll', runAnchorTopperAlignment, true);
    };
  }, [floatingTopperAlign, isOpen, runAnchorTopperAlignment]);

  return (
    <Popover
      id={anchorElId}
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      // Body scroll-lock reserves/compensates for the viewport scrollbar; on Windows that often reads as a bright blue strip.
      disableScrollLock
      onClose={() => {
        handleClose();
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      slotProps={{
        transition: {
          onEntered: () => {
            runAnchorTopperAlignment();
            recalcAdaptiveScrollbarGutter();
          },
        },
        paper: {
          elevation: 8,
          sx: {
            minWidth: '190px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            boxSizing: 'border-box',
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.darkGray.main}`,
            borderRadius: MENU_PANEL_CORNER_RADIUS,
            // PopoverPaper defaults to overflowY: auto and overflowX: hidden, which clips the
            // absolutely positioned floating topper; set both axes explicitly.
            overflowX: 'visible',
            overflowY: 'visible',
            boxShadow: `0 4px 8px 0 ${theme.palette.black.main}1A`,
            marginTop: `${FLOATING_MENU_TOPPER_HEIGHT_PX}px`,
            maxHeight: 'none',
          },
        },
      }}
      sx={{
        '& .MuiMenuItem-root': {
          padding: '8px 16px',
          color: theme.palette.text.primary,
        },
      }}
    >
      <Box
        ref={chromeRef}
        sx={{
          position: 'relative',
          overflow: 'visible',
          width: '100%',
          minWidth: '100%',
          boxSizing: 'border-box',
          flex: '1 1 auto',
        }}
      >
        <Box
          ref={notchRef}
          data-testid="menu-floating-topper-notch"
          sx={{
            position: 'absolute',
            top: `-${FLOATING_MENU_TOPPER_HEIGHT_PX}px`,
            zIndex: 1,
            pointerEvents: 'none',
            flexShrink: 0,
            ...(floatingTopperAlign === 'panelCenter'
              ? { left: '50%', right: 'auto', transform: 'translateX(-50%)' }
              : {}),
          }}
          role="presentation"
        >
          <HXCIcon
            name={Icons.FloatingMenuTopper}
            sizePx={FLOATING_MENU_TOPPER_WIDTH_PX}
            color="darkGray"
            sx={{
              display: 'block',
              '& path:nth-of-type(1)': {
                fill: theme.palette.background.paper,
              },
              '& path:nth-of-type(2)': {
                fill: 'none',
                stroke: theme.palette.darkGray.main,
              },
            }}
          />
        </Box>
        <Box
          ref={scrollPortRef}
          sx={{
            width: '100%',
            minWidth: 0,
            maxHeight: scrollableMaxHeight,
            overflowY: 'auto',
            overflowX: 'hidden',
            scrollbarGutter: resolvedScrollbarGutter,
            borderRadius: MENU_PANEL_SCROLL_INNER_CORNER_RADIUS,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {menuList}
        </Box>
      </Box>
    </Popover>
  );
};
